import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

const QUOTE_LEAD_FORM_URL = 'https://readdy.ai/api/form/d6rj47ua739gopmlhmm0';

export default function InstantQuotePage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    health: '',
    tobacco: '',
    coverage: '',
    term: '',
    policyType: 'term',
  });
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);

  // Lead capture state
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [smsConsent, setSmsConsent] = useState(false);
  const [leadStatus, setLeadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [leadSkipped, setLeadSkipped] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Instant Life Insurance Quote Indiana | Hoosier Life';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Get an instant life insurance quote in 60 seconds. Free online calculator for term, whole life & universal life rates in Indiana. No personal info needed.');
    setMeta('keywords', 'instant life insurance quote, life insurance calculator, free quote Indiana, term life rates, whole life cost, life insurance estimator');
    setMeta('og:title', 'Instant Life Insurance Quote Indiana | Free Calculator', true);
    setMeta('og:description', 'Calculate your life insurance cost in 60 seconds. Free instant quotes for term, whole, and universal life insurance in Indiana.', true);
    setMeta('og:url', `${SITE_URL}/instant-quote`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/instant-quote`);

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Instant Quote", "item": `${SITE_URL}/instant-quote` }
      ]
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Instant Life Insurance Quote Calculator",
      "description": "Get an instant life insurance quote in 60 seconds. Free online calculator for term life, whole life, and universal life insurance rates in Indiana.",
      "url": `${SITE_URL}/instant-quote`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Instant Quote", "item": `${SITE_URL}/instant-quote` }
        ]
      }
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Life Insurance Quote Calculator",
      "name": "Instant Life Insurance Quote Tool",
      "description": "Free online calculator providing instant life insurance quotes for term, whole, and universal life insurance policies in Indiana.",
      "provider": {
        "@type": "InsuranceAgency",
        "name": "Hoosier Life Insurance",
        "url": SITE_URL,
        "areaServed": {
          "@type": "State",
          "name": "Indiana"
        }
      },
      "areaServed": {
        "@type": "State",
        "name": "Indiana"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free instant life insurance quote calculator"
      }
    };

    let schemaScript = document.querySelector('script[data-schema="instant-quote"]') as HTMLScriptElement | null;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'instant-quote');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify([breadcrumbSchema, webPageSchema, serviceSchema]);
  }, [SITE_URL]);

  const calculateEstimate = () => {
    const age = parseInt(formData.age);
    const coverage = parseInt(formData.coverage);
    if (!age || !coverage) return;
    let baseRate = 0.05;
    if (age < 30) baseRate *= 0.6;
    else if (age < 40) baseRate *= 0.8;
    else if (age < 50) baseRate *= 1.2;
    else if (age < 60) baseRate *= 1.8;
    else baseRate *= 2.5;
    if (formData.gender === 'female') baseRate *= 0.85;
    if (formData.health === 'excellent') baseRate *= 0.9;
    else if (formData.health === 'good') baseRate *= 1.0;
    else if (formData.health === 'average') baseRate *= 1.3;
    else if (formData.health === 'poor') baseRate *= 1.8;
    if (formData.tobacco === 'yes') baseRate *= 2.0;
    if (formData.policyType === 'whole') baseRate *= 8;
    else if (formData.policyType === 'universal') baseRate *= 6;
    const monthlyEstimate = (coverage / 1000) * baseRate;
    setEstimatedPrice(Math.round(monthlyEstimate));
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      calculateEstimate();
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleReset = () => {
    setStep(1);
    setFormData({ age: '', gender: '', health: '', tobacco: '', coverage: '', term: '', policyType: 'term' });
    setEstimatedPrice(null);
    setLeadData({ name: '', email: '', phone: '' });
    setSmsConsent(false);
    setLeadStatus('idle');
    setLeadSkipped(false);
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLeadStatus('loading');
    try {
      // Post to Readdy form
      const body = new URLSearchParams();
      body.append('name', leadData.name);
      body.append('email', leadData.email);
      body.append('phone', leadData.phone);
      body.append('estimated_premium', `$${estimatedPrice}/month`);
      body.append('policy_type', formData.policyType);
      body.append('coverage_amount', formData.coverage);
      body.append('age', formData.age);
      body.append('health', formData.health);
      body.append('tobacco', formData.tobacco);
      body.append('sms_consent', smsConsent ? 'Yes' : 'No');
      await fetch(QUOTE_LEAD_FORM_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      // Insert lead into Supabase
      const nameParts = leadData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const followUpTime = new Date();
      followUpTime.setHours(followUpTime.getHours() + 24);

      supabase.from('leads').insert({
        first_name: firstName,
        last_name: lastName,
        email: leadData.email,
        phone: leadData.phone,
        age_range: formData.age,
        goals: formData.policyType,
        coverage_amount: formData.coverage,
        notes: `Instant Quote: $${estimatedPrice}/mo for $${parseInt(formData.coverage).toLocaleString()} ${formData.policyType} life. Health: ${formData.health}. Tobacco: ${formData.tobacco}.`,
        source: 'Instant Quote Tool',
        stage: 'new',
        follow_up_scheduled_at: followUpTime.toISOString(),
        follow_up_sent: false,
      }).then(() => {}).catch(() => {});

      // Send agent notification email
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'new_lead',
          lead: {
            name: leadData.name,
            email: leadData.email,
            phone: leadData.phone,
            service: `${formData.policyType} life — $${parseInt(formData.coverage).toLocaleString()} coverage`,
            message: `Instant Quote result: ~$${estimatedPrice}/month. Age: ${formData.age}, Health: ${formData.health}, Tobacco: ${formData.tobacco}.`,
          },
        }),
      }).catch(() => {});

      setLeadStatus('success');
    } catch {
      setLeadStatus('error');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FDF6F0] via-white to-[#F9F7F4] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-flashlight-line text-lg"></i>
              Get Your Estimate in 60 Seconds
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Instant Life Insurance Quote Calculator
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              See how much life insurance costs in Indiana. No personal information required—just answer a few quick questions to get your <strong>free instant estimate</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Calculator */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= num ? 'bg-[#D97642] text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {num}
                  </div>
                  {num < 4 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      step > num ? 'bg-[#D97642]' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Basic Info</span>
              <span>Health</span>
              <span>Coverage</span>
              <span>Results</span>
            </div>
          </div>

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="bg-gradient-to-br from-[#FDF6F0] to-white rounded-2xl p-10 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Tell Us About Yourself</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">What is your age?</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    placeholder="Enter your age"
                    className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#D97642] focus:outline-none"
                    min="18"
                    max="80"
                  />
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">What is your gender?</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setFormData({...formData, gender: 'male'})}
                      className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                        formData.gender === 'male'
                          ? 'bg-[#D97642] text-white border-[#D97642]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#D97642]'
                      }`}
                    >
                      <i className="ri-user-line mr-2"></i>
                      Male
                    </button>
                    <button
                      onClick={() => setFormData({...formData, gender: 'female'})}
                      className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                        formData.gender === 'female'
                          ? 'bg-[#D97642] text-white border-[#D97642]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#D97642]'
                      }`}
                    >
                      <i className="ri-user-line mr-2"></i>
                      Female
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button
                  onClick={handleNext}
                  disabled={!formData.age || !formData.gender}
                  className="bg-[#D97642] hover:bg-[#C26638] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Next Step
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Health */}
          {step === 2 && (
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-2xl p-10 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Health Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">How would you rate your overall health?</label>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { value: 'excellent', label: 'Excellent', icon: 'ri-heart-pulse-line' },
                      { value: 'good', label: 'Good', icon: 'ri-heart-line' },
                      { value: 'average', label: 'Average', icon: 'ri-heart-2-line' },
                      { value: 'poor', label: 'Fair/Poor', icon: 'ri-heart-3-line' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormData({...formData, health: option.value})}
                        className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                          formData.health === option.value
                            ? 'bg-[#1A3A52] text-white border-[#1A3A52]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#1A3A52]'
                        }`}
                      >
                        <i className={`${option.icon} mr-2`}></i>
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">Do you use tobacco products?</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setFormData({...formData, tobacco: 'no'})}
                      className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                        formData.tobacco === 'no'
                          ? 'bg-[#1A3A52] text-white border-[#1A3A52]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#1A3A52]'
                      }`}
                    >
                      <i className="ri-close-circle-line mr-2"></i>
                      No
                    </button>
                    <button
                      onClick={() => setFormData({...formData, tobacco: 'yes'})}
                      className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                        formData.tobacco === 'yes'
                          ? 'bg-[#1A3A52] text-white border-[#1A3A52]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#1A3A52]'
                      }`}
                    >
                      <i className="ri-check-line mr-2"></i>
                      Yes
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-10 py-4 rounded-lg font-bold text-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={!formData.health || !formData.tobacco}
                  className="bg-[#1A3A52] hover:bg-[#0F2A3F] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Next Step
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Coverage */}
          {step === 3 && (
            <div className="bg-gradient-to-br from-[#FDF6F0] to-white rounded-2xl p-10 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Coverage Details</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">What type of policy do you want?</label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: 'term', label: 'Term Life' },
                      { value: 'whole', label: 'Whole Life' },
                      { value: 'universal', label: 'Universal Life' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setFormData({...formData, policyType: option.value})}
                        className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                          formData.policyType === option.value
                            ? 'bg-[#D97642] text-white border-[#D97642]'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#D97642]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">How much coverage do you need?</label>
                  <select
                    value={formData.coverage}
                    onChange={(e) => setFormData({...formData, coverage: e.target.value})}
                    className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-lg focus:border-[#D97642] focus:outline-none"
                  >
                    <option value="">Select coverage amount</option>
                    <option value="100000">$100,000</option>
                    <option value="250000">$250,000</option>
                    <option value="500000">$500,000</option>
                    <option value="750000">$750,000</option>
                    <option value="1000000">$1,000,000</option>
                    <option value="1500000">$1,500,000</option>
                    <option value="2000000">$2,000,000</option>
                  </select>
                </div>

                {formData.policyType === 'term' && (
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-3">What term length do you prefer?</label>
                    <div className="grid grid-cols-4 gap-4">
                      {['10', '15', '20', '30'].map((years) => (
                        <button
                          key={years}
                          onClick={() => setFormData({...formData, term: years})}
                          className={`px-6 py-4 text-lg font-semibold rounded-lg border-2 transition-all ${
                            formData.term === years
                              ? 'bg-[#D97642] text-white border-[#D97642]'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-[#D97642]'
                          }`}
                        >
                          {years} Years
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-10 py-4 rounded-lg font-bold text-lg transition-all whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={!formData.coverage || (formData.policyType === 'term' && !formData.term)}
                  className="bg-[#D97642] hover:bg-[#C26638] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Calculate Quote
                  <i className="ri-calculator-line ml-2"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Results + Lead Capture */}
          {step === 4 && estimatedPrice !== null && (
            <div className="space-y-6">
              {/* Quote Result Card */}
              <div className="bg-gradient-to-br from-[#FDF6F0] to-white rounded-2xl p-10 shadow-lg">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <i className="ri-check-line text-lg"></i>
                    Your Instant Estimate
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Estimated Monthly Premium</h2>
                  <div className="text-6xl font-bold text-[#D97642] mb-2">
                    ${estimatedPrice}
                    <span className="text-2xl text-gray-600">/month</span>
                  </div>
                  <p className="text-lg text-gray-600">
                    For ${parseInt(formData.coverage).toLocaleString()} in{' '}
                    {formData.policyType === 'term' ? `${formData.term}-year term` : formData.policyType} life insurance coverage
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Your Quote Details</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-gray-700">
                    <div className="flex justify-between"><span className="font-semibold">Age:</span><span>{formData.age} years old</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Gender:</span><span className="capitalize">{formData.gender}</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Health:</span><span className="capitalize">{formData.health}</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Tobacco:</span><span>{formData.tobacco === 'yes' ? 'Yes' : 'No'}</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Policy Type:</span><span className="capitalize">{formData.policyType === 'term' ? 'Term Life' : formData.policyType === 'whole' ? 'Whole Life' : 'Universal Life'}</span></div>
                    <div className="flex justify-between"><span className="font-semibold">Coverage:</span><span>${parseInt(formData.coverage).toLocaleString()}</span></div>
                  </div>
                </div>

                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-2xl text-amber-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">This is an estimate only</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Your actual premium may vary based on detailed health information, medical exam results, and specific policy features. Schedule a free consultation to get an accurate personalized quote.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 justify-center">
                  <Link to="/book-appointment" className="bg-[#D97642] hover:bg-[#C26638] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all shadow-lg whitespace-nowrap">
                    Get Accurate Quote
                  </Link>
                  <button onClick={handleReset} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-10 py-4 rounded-lg font-bold text-lg transition-all whitespace-nowrap">
                    <i className="ri-refresh-line mr-2"></i>Start Over
                  </button>
                </div>
              </div>

              {/* Lead Capture Card */}
              {!leadSkipped && (
                <div className="bg-white rounded-2xl shadow-lg border-2 border-[#D97642]/30 p-8">
                  {leadStatus === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-16 h-16 flex items-center justify-center bg-[#D97642]/10 rounded-full mb-4">
                        <i className="ri-check-line text-[32px] text-[#D97642]"></i>
                      </div>
                      <h3 className="text-[22px] font-bold text-gray-900 mb-2">We'll Be in Touch!</h3>
                      <p className="text-gray-600 max-w-sm">
                        A licensed Indiana agent will reach out with your personalized accurate quote — usually within a few hours.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-full shrink-0">
                          <i className="ri-mail-send-line text-[20px] text-[#D97642]"></i>
                        </div>
                        <h3 className="text-[20px] font-bold text-gray-900">Get Your Accurate Quote Sent to You</h3>
                      </div>
                      <p className="text-gray-600 text-sm mb-6 ml-[52px]">
                        A licensed Indiana agent will review your estimate and send you a real, personalized quote — no obligation.
                      </p>
                      <form
                        id="instant-quote-lead-form"
                        data-readdy-form
                        onSubmit={handleLeadSubmit}
                        className="space-y-4"
                      >
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Full Name <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              required
                              placeholder="Jane Smith"
                              value={leadData.name}
                              onChange={(e) => setLeadData((p) => ({ ...p, name: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Email <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              placeholder="jane@example.com"
                              value={leadData.email}
                              onChange={(e) => setLeadData((p) => ({ ...p, email: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                              Phone <span className="text-[#D97642]">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              required
                              placeholder="(317) 000-0000"
                              value={leadData.phone}
                              onChange={(e) => setLeadData((p) => ({ ...p, phone: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                            />
                          </div>
                        </div>

                        {/* SMS Consent */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              name="sms_consent"
                              required
                              checked={smsConsent}
                              onChange={(e) => setSmsConsent(e.target.checked)}
                              className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer"
                            />
                            <span className="text-[12px] text-gray-600 leading-relaxed">
                              <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance regarding my quote request and insurance information. Message &amp; data rates may apply. Reply STOP to opt out at any time.{' '}
                              <a href="/privacy-policy" className="text-[#D97642] underline hover:no-underline">Privacy Policy</a>
                            </span>
                          </label>
                        </div>

                        {leadStatus === 'error' && (
                          <p className="text-sm text-red-500 flex items-center gap-1.5">
                            <i className="ri-error-warning-line"></i>
                            Something went wrong. Please try again.
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="submit"
                            disabled={leadStatus === 'loading' || !smsConsent}
                            className="flex-1 min-w-[200px] py-3.5 bg-[#D97642] hover:bg-[#C26638] text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {leadStatus === 'loading' ? (
                              <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                            ) : (
                              <><i className="ri-send-plane-fill"></i> Send Me My Accurate Quote</>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setLeadSkipped(true)}
                            className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap underline"
                          >
                            No thanks
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Why Get a Quote */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Get a Life Insurance Quote?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding your life insurance costs helps you make informed decisions about protecting your family
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Protect Your Family</h3>
              <p className="text-gray-700">
                Ensure your loved ones can maintain their lifestyle and pay off debts if something happens to you.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Budget Planning</h3>
              <p className="text-gray-700">
                Know exactly how much coverage you can afford and plan your family's financial future accordingly.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lock In Low Rates</h3>
              <p className="text-gray-700">
                Life insurance costs increase with age. Getting a quote now helps you secure lower rates while younger and healthier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready for an Accurate Quote?</h2>
          <p className="text-xl mb-10 opacity-95 leading-relaxed">
            Talk to an Indiana-licensed agent who can provide a personalized quote based on your specific situation and answer all your questions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment" className="bg-[#D97642] hover:bg-[#C26638] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all shadow-lg whitespace-nowrap">
              Book Free Consultation
            </Link>
            <div className="text-center">
              <a
                href="tel:3176482983"
                className="inline-flex items-center justify-center gap-2 bg-[#D97642] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#C26638] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call Now: (317) 648-2983
              </a>
            </div>
          </div>
          <p className="mt-8 text-sm opacity-90">
            Serving Indianapolis, Fort Wayne, Carmel, Fishers, and all of Indiana
          </p>
        </div>
      </section>
    </>
  );
}